import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from './supabase-client';
import { batchSignUrls } from './gcs-client';

export interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  subscription_status: string | null;
  available_credits: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isPasswordRecovery: boolean;
  refreshProfile: () => Promise<void>;
  clearPasswordRecovery: () => void;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, newsletterSubscribed?: boolean) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (_event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const refreshProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles' as any)
      .select('id, first_name, last_name, avatar_url, subscription_status, available_credits')
      .eq('id', user.id)
      .single();

    if (data) {
      let signedProfile = { ...data };

      // Sign avatar_url if it's a private path
      if (signedProfile.avatar_url && !signedProfile.avatar_url.startsWith('http')) {
        const signedUrls = await batchSignUrls([
          { bucket: 'wysko-assets', path: signedProfile.avatar_url }
        ]);

        if (signedUrls[signedProfile.avatar_url]) {
          signedProfile.avatar_url = signedUrls[signedProfile.avatar_url];
        }
      }

      setProfile(signedProfile as UserProfile);
    }
  };

  // Initial profile fetch
  useEffect(() => {
    if (user) {
      refreshProfile();
    } else {
      setProfile(null);
    }
  }, [user]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    return { error };
  };

  const signUp = async (email: string, password: string, newsletterSubscribed?: boolean) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { newsletter_subscribed: newsletterSubscribed ?? false },
      },
    });
    return { error };
  };

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  // S1-013 FIX: Centralized admin check (single source of truth)
  const adminEmails = (import.meta.env.VITE_ADMIN_EMAILS || import.meta.env.VITE_ADMIN_EMAIL || '')
    .split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean);
  const isAdmin = !!user?.email && adminEmails.includes(user.email.toLowerCase());

  const value = {
    user,
    session,
    profile,
    loading,
    isAdmin,
    isPasswordRecovery,
    refreshProfile,
    clearPasswordRecovery,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
